import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../components/Sidebar';
import { T } from '../styles/tokens';

const wgSteps: Record<string, string[]> = {
  ios: ['Откройте App Store → найдите «WireGuard» → установите', 'Откройте бота, нажмите «Купить» → WireGuard → выберите тариф', 'Получите QR-код или .conf файл', 'В приложении: «+» → «Создать из QR-кода»', 'Включите тумблер — готово'],
  android: ['Откройте Google Play → «WireGuard» → установите', 'Откройте бота → «Купить» → WireGuard', 'Получите QR-код или .conf файл', 'В приложении: «+» → «Сканировать QR» или «Импортировать файл»', 'Включите тумблер — готово'],
  windows: ['Скачайте WireGuard с wireguard.com/install/', 'Установите приложение', 'Откройте бота → «Купить» → WireGuard → получите wg.conf', 'В приложении: «Импортировать туннель из файла»', 'Нажмите «Активировать» — готово'],
  macos: ['Откройте App Store → «WireGuard» → установите', 'Получите .conf из бота после покупки', 'В приложении: «Импортировать туннель из файла»', 'Включите тумблер — готово'],
};

const vlessSteps: Record<string, string[]> = {
  ios: ['Откройте App Store → «Streisand» или «V2Box» → установите', 'Откройте бота → «Купить» → VLESS Reality → выберите тариф', 'Скопируйте vless://... ссылку или QR', 'В приложении: «+» → «Импорт из буфера обмена»', 'Подключитесь — готово'],
  android: ['Откройте Google Play → «v2rayNG» → установите', 'Откройте бота → «Купить» → VLESS Reality', 'Скопируйте ссылку vless://...', 'В v2rayNG: «+» → «Импорт из буфера обмена»', 'Нажмите кнопку подключения — готово'],
  windows: ['Скачайте Hiddify с hiddify.com → установите', 'Откройте бота → «Купить» → VLESS Reality', 'Скопируйте ссылку vless://...', 'В Hiddify: «+» → вставьте ссылку', 'Нажмите «Подключиться» — готово'],
  macos: ['Скачайте Hiddify или V2Box → установите', 'Откройте бота → «Купить» → VLESS Reality', 'Скопируйте ссылку vless://...', 'Добавьте конфиг через импорт ссылки', 'Подключитесь — готово'],
};

const osList = ['ios', 'android', 'windows', 'macos'] as const;
const osLabels: Record<string, string> = { ios: 'iOS / iPadOS', android: 'Android', windows: 'Windows', macos: 'macOS' };

export default function Instructions() {
  const nav = useNavigate();
  const [proto, setProto] = useState<'wireguard' | 'vless'>('wireguard');
  const [os, setOs] = useState<string>('ios');
  const steps = proto === 'wireguard' ? wgSteps[os] : vlessSteps[os];

  return (
    <div style={e.root}>
      <Sidebar active="instructions" />
      <main style={e.main}>
        <div style={e.topBar}>
          <div>
            <div style={e.pageTag}>// руководство</div>
            <div style={e.pageTitle}>Инструкции по подключению</div>
          </div>
          <button style={e.ctaBtn} onClick={() => nav('/purchase')}>
            Купить подписку
          </button>
        </div>

        <div style={e.protoTabs}>
          {([['wireguard', 'WireGuard'], ['vless', 'VLESS Reality']] as const).map(([id, label]) => (
            <button key={id} style={{ ...e.protoTab, ...(proto === id ? e.protoTabActive : {}) }} onClick={() => setProto(id)}>
              {label}
            </button>
          ))}
        </div>

        <div style={e.instrLayout}>
          <div style={e.osList}>
            <div style={e.osListLabel}>// платформа</div>
            {osList.map((o) => (
              <button key={o} style={{ ...e.osBtn, ...(os === o ? e.osBtnActive : {}) }} onClick={() => setOs(o)}>
                <span style={e.osMono}>{o.toUpperCase()}</span>
                <span>{osLabels[o]}</span>
              </button>
            ))}
          </div>
          <div style={e.stepsCard}>
            <div style={e.stepsHeader}>
              <div style={e.stepsProtoTag}>{proto === 'wireguard' ? 'WireGuard' : 'VLESS Reality'}</div>
              <div style={e.stepsOs}>{osLabels[os]}</div>
            </div>
            <div style={e.stepsList}>
              {steps.map((step, i) => (
                <div key={i} style={e.stepItem}>
                  <div style={e.stepNum}>{String(i + 1).padStart(2, '0')}</div>
                  <div style={e.stepText}>{step}</div>
                </div>
              ))}
            </div>
            <div style={e.appsRow}>
              <div style={e.appsLabel}>// приложения</div>
              <div style={e.appLinks}>
                {(proto === 'wireguard'
                  ? ([['App Store (iOS)', 'https://apps.apple.com/app/wireguard/id1441195209'], ['Google Play', 'https://play.google.com/store/apps/details?id=com.wireguard.android'], ['wireguard.com', 'https://www.wireguard.com/install/']] as const)
                  : ([['Streisand (iOS)', 'https://apps.apple.com/app/streisand/id6450534064'], ['v2rayNG (Android)', 'https://play.google.com/store/apps/details?id=com.v2ray.ang'], ['Hiddify', 'https://hiddify.com']] as const)
                ).map(([lbl, url]) => (
                  <a key={lbl} href={url} target="_blank" rel="noreferrer" style={e.appLink}>
                    {lbl} →
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const e: Record<string, CSSProperties> = {
  root: { display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: 'DM Sans, sans-serif', color: T.text },
  main: { flex: 1, marginLeft: 220, padding: '36px 44px', boxSizing: 'border-box', maxWidth: 'calc(100vw - 220px)' },
  topBar: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 },
  pageTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 },
  pageTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 28, letterSpacing: '-0.03em', color: T.text },
  ctaBtn: { background: T.accent, border: 'none', borderRadius: 4, padding: '11px 22px', color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  protoTabs: { display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${T.border}`, paddingBottom: 0 },
  protoTab: { background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: T.textSec, fontFamily: 'DM Sans, sans-serif', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '12px 18px' },
  protoTabActive: { color: T.accent, borderBottomColor: T.accent },
  instrLayout: { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24 },
  osList: { display: 'flex', flexDirection: 'column', gap: 4 },
  osListLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10, paddingLeft: 8 },
  osBtn: { display: 'flex', alignItems: 'center', gap: 12, background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 3, padding: '11px 14px', color: T.textSec, fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer', textAlign: 'left' },
  osBtnActive: { background: T.accentSoft, color: T.accent, borderColor: T.accent },
  osMono: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, minWidth: 50 },
  stepsCard: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '24px 28px' },
  stepsHeader: { display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${T.border}` },
  stepsProtoTag: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em', color: T.text },
  stepsOs: { fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.textMuted, letterSpacing: '0.04em' },
  stepsList: { display: 'flex', flexDirection: 'column', gap: 14 },
  stepItem: { display: 'flex', gap: 16, alignItems: 'flex-start' },
  stepNum: { fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.accent, letterSpacing: '0.04em', width: 24, flexShrink: 0, paddingTop: 2 },
  stepText: { fontSize: 14, color: T.textSec, lineHeight: 1.6 },
  appsRow: { marginTop: 28, paddingTop: 20, borderTop: `1px solid ${T.border}` },
  appsLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 },
  appLinks: { display: 'flex', gap: 20, flexWrap: 'wrap' },
  appLink: { fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: T.accent, textDecoration: 'none', fontWeight: 600 },
};
